import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Progress,
} from "reactstrap";

import { H3, H4, H5, P, Btn } from "../../AbstractElements";
import FileHandler from "../../CommonElements/FileHandler";
import FixedFooter from "../../CommonElements/FixedFooter";
import MUIIcons from "../../CommonElements/MUIIcon/MUIIcons";
import StarRating from "../../CommonElements/StarRating";
import ToolTip from "../../CommonElements/Tooltip";
import VersionControl from "../../container/VersionControl";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { getSurveyDetail } from "../../ReduxToolkit/Reducers/SurveySlice";
import "./SurveyPreview.scss";

const LIKERT_SCALE_VALUES = [1, 2, 3, 4, 5, 6];
const LIKERT_LABELS = [
  "Strongly Disagree",
  "Disagree",
  "Somewhat Disagree",
  "Somewhat Agree",
  "Agree",
  "Strongly Agree",
];

const SurveyPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { surveyDetails } = useAppSelector(state => state.survey);
  const [answers, setAnswers] = useState<any>({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [touched, setTouched] = useState(false);
  const [isShowVersionModal, setIsShowVersionModal] = useState(false);

  const id = new URLSearchParams(location.search).get("id");

  useEffect(() => {
    if (id) {
      dispatch(getSurveyDetail(Number(id)));
    }
  }, [id, dispatch]);

  const allQuestions = useMemo(() => {
    if (!surveyDetails?.sections) return [];
    return surveyDetails.sections.flatMap(section =>
      (section.questions || []).map(q => ({
        ...q,
        sectionId: section.id,
        sectionTitle: section.title,
        sectionDescription: section.description,
      }))
    );
  }, [surveyDetails]);

  const visibleQuestions = useMemo(() => {
    if (
      !surveyDetails?.branching_rules ||
      surveyDetails.branching_rules.length === 0
    ) {
      return allQuestions;
    }
    const result: any[] = [];
    const visited = new Set();
    let idx = 0;
    while (idx < allQuestions.length) {
      const q = allQuestions[idx];
      if (visited.has(q.id)) break;
      visited.add(q.id);
      result.push(q);
      const rules = surveyDetails.branching_rules.filter(
        r => String(r.parent_question_id) === String(q.id)
      );
      const ans = answers[q.id];
      let nextId: string | undefined = undefined;
      if (rules.length > 0 && ans !== undefined && ans !== null) {
        for (const rule of rules) {
          if (Array.isArray(ans)) {
            if (ans.includes(rule.option_id)) {
              nextId = String(rule.child_question_id);
              break;
            }
          } else {
            if (String(rule.option_id) === String(ans)) {
              nextId = String(rule.child_question_id);
              break;
            }
          }
        }
      }
      if (nextId) {
        const nextIdx = allQuestions.findIndex(qq => String(qq.id) === nextId);
        if (nextIdx !== -1) {
          idx = nextIdx;
          continue;
        }
      }
      idx++;
    }
    return result;
  }, [allQuestions, answers, surveyDetails]);

  useEffect(() => {
    if (currentIdx > visibleQuestions.length - 1) {
      setCurrentIdx(Math.max(visibleQuestions.length - 1, 0));
    }
  }, [visibleQuestions, currentIdx]);

  useEffect(() => {
    setAnswers(prev => {
      const next: any = {};
      for (const q of visibleQuestions) {
        if (prev[q.id] !== undefined) next[q.id] = prev[q.id];
      }
      return next;
    });
  }, [visibleQuestions.map(q => q.id).join(",")]);

  const currentQuestion = visibleQuestions[currentIdx];
  const total = visibleQuestions.length;
  const progress = Math.round(((currentIdx + 1) / total) * 100);

  const handleChange = (question, value) => {
    setAnswers(prev => ({
      ...prev,
      [question.id]: value,
    }));
    setTouched(false);
  };

  const handleNext = () => {
    setTouched(true);
    if (!isValid(currentQuestion)) return;
    setCurrentIdx(idx => Math.min(idx + 1, total - 1));
    setTouched(false);
  };

  const handleBack = () => {
    setCurrentIdx(idx => Math.max(idx - 1, 0));
    setTouched(false);
  };

  const handleClose = () => {
    navigate("/pending-for-approval");
  };

  const isValid = question => {
    if (!question) return true;
    if (!question.is_required) return true;
    const val = answers[question.id];
    if (question.question_type === "multi_select") {
      return Array.isArray(val) && val.length > 0;
    }
    if (question.question_type === "likert_scale") {
      if (!val || typeof val !== "object") return false;
      return question.options.every(opt => val[opt.id]);
    }
    return val !== undefined && val !== null && val !== "";
  };

  const handleVersionModal = () => {
    setIsShowVersionModal(prev => !prev);
  };

  const handleClickVersion = element => {
    handleVersionModal();
    return navigate(`/survey-preview?id=${element.id}`);
  };

  const renderQuestionInput = q => {
    switch (q.question_type) {
      case "open_text":
        return (
          <Input
            type="textarea"
            value={answers[q.id] || ""}
            onChange={e => handleChange(q, e.target.value)}
            placeholder="Your answer"
          />
        );
      case "single_select":
        return (
          <div>
            {q.options?.map(opt => (
              <FormGroup check key={opt.id} className="mb-2">
                <Input
                  type="radio"
                  name={`q_${q.id}`}
                  value={opt.id}
                  checked={answers[q.id] === opt.id}
                  onChange={() => handleChange(q, opt.id)}
                />
                <Label check className="ms-2">
                  {opt.option_text}
                </Label>
              </FormGroup>
            ))}
          </div>
        );
      case "multi_select":
        return (
          <div>
            {q.options?.map(opt => (
              <FormGroup check key={opt.id} className="mb-2">
                <Input
                  className="primary-checkbox"
                  type="checkbox"
                  name={`q_${q.id}`}
                  value={opt.id}
                  checked={
                    Array.isArray(answers[q.id]) &&
                    answers[q.id].includes(opt.id)
                  }
                  onChange={e => {
                    const prev = Array.isArray(answers[q.id])
                      ? answers[q.id]
                      : [];
                    if (e.target.checked) {
                      handleChange(q, [...prev, opt.id]);
                    } else {
                      handleChange(
                        q,
                        prev.filter(id => id !== opt.id)
                      );
                    }
                  }}
                />
                <Label check className="ms-2">
                  {opt.option_text}
                </Label>
              </FormGroup>
            ))}
          </div>
        );
      case "dropdown":
        return (
          <Input
            type="select"
            value={answers[q.id] || ""}
            onChange={e => handleChange(q, e.target.value)}
          >
            <option value="">Select an option</option>
            {q.options?.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.option_text}
              </option>
            ))}
          </Input>
        );
      case "rating":
        return (
          <div className="d-flex align-items-center gap-2">
            <StarRating
              size={32}
              count={5}
              initialValue={answers[q.id] || 0}
              onClick={val => handleChange(q, val)}
            />
            {answers[q.id] && <span className="ms-2">{answers[q.id]} / 5</span>}
          </div>
        );
      case "likert_scale":
        return (
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th></th>
                  {LIKERT_LABELS.map((label, idx) => (
                    <th key={idx} style={{ minWidth: 80, textAlign: "center" }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.options?.map(opt => (
                  <tr key={opt.id}>
                    <td>{opt.option_text}</td>
                    {LIKERT_SCALE_VALUES.map(val => (
                      <td key={val} style={{ textAlign: "center" }}>
                        <Input
                          type="radio"
                          name={`likert_${q.id}_${opt.id}`}
                          checked={answers[q.id]?.[opt.id] === val}
                          onChange={() => {
                            handleChange(q, {
                              ...answers[q.id],
                              [opt.id]: val,
                            });
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "ranking":
        return (
          <Row>
            {q.options?.map(opt => (
              <Col key={opt.id} md={6} className="mb-2">
                <Label>Rank for {opt.option_text}</Label>
                <Input
                  type="number"
                  min={1}
                  max={q.options.length}
                  value={answers[q.id]?.[opt.id] || ""}
                  onChange={e => {
                    const prev = answers[q.id] || {};
                    handleChange(q, {
                      ...prev,
                      [opt.id]: Number(e.target.value),
                    });
                  }}
                />
              </Col>
            ))}
          </Row>
        );
      case "date_picker":
        return (
          <Input
            type="date"
            value={answers[q.id] || ""}
            onChange={e => handleChange(q, e.target.value)}
          />
        );
      default:
        return <P>Unsupported question type</P>;
    }
  };

  return (
    <div className="page-body d-flex pb-5">
      <div style={{ width: "100%" }}>
        <Card
          className="shadow rounded-4 border-0 p-0"
          style={{ marginTop: 40 }}
        >
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <H3 className="mb-0">Survey Preview</H3>
            <span id="SurveyVersionIcon" onClick={handleVersionModal}>
              <MUIIcons
                iconName="History"
                className="primary-icon-color"
                size={28}
              />
            </span>
            <ToolTip placement="left" target="SurveyVersionIcon">
              Show Version History
            </ToolTip>
          </div>
          <VersionControl
            isShowVersionModal={isShowVersionModal}
            handleVersionModal={handleVersionModal}
            versions={surveyDetails?.survey_versions || []}
            handleClickVersion={handleClickVersion}
            activeId={Number(id)}
          />
          <CardBody className="p-4">
            {surveyDetails && visibleQuestions.length > 0 && currentQuestion ? (
              <>
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-bold">Progress</span>
                    <span className="text-muted">
                      {currentIdx + 1} / {total}
                    </span>
                  </div>
                  <Progress
                    value={progress}
                    color="primary"
                    style={{ height: 8, borderRadius: 8 }}
                  />
                </div>
                <H3 className="mb-2 text-center">{surveyDetails.title}</H3>
                <P className="mb-4 text-center">{surveyDetails.description}</P>
                <div className="survey-section-bg mb-4 p-4 rounded-3">
                  <div className="survey-section-header mb-3 pb-2">
                    <H4 className="survey-section-title mb-1">
                      {currentQuestion.sectionTitle}
                    </H4>
                    {currentQuestion.sectionDescription && (
                      <P className="survey-section-desc mb-0 text-muted">
                        {currentQuestion.sectionDescription}
                      </P>
                    )}
                  </div>
                  <FormGroup className="mb-0">
                    <div className="survey-question-block mb-3">
                      <H5 className="survey-question-title mb-2">
                        {currentQuestion.title}
                        {currentQuestion.is_required && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </H5>
                      {currentQuestion.description && (
                        <P className="survey-question-desc mb-2 text-secondary">
                          {currentQuestion.description}
                        </P>
                      )}
                      {currentQuestion.media_url && (
                        <div className="survey-media mb-3">
                          <FileHandler
                            mediaUrl={currentQuestion.media_url}
                            extension={currentQuestion.extension}
                            fileName={currentQuestion.fileName}
                            showPreview={true}
                          />
                        </div>
                      )}
                      <div className="survey-question-input pt-2 pb-2">
                        {renderQuestionInput(currentQuestion)}
                      </div>
                      {touched && !isValid(currentQuestion) && (
                        <div className="text-danger mt-2">
                          This question is required.
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-4 gap-2">
                  <Btn
                    outline
                    color="primary"
                    onClick={handleBack}
                    disabled={currentIdx === 0}
                  >
                    Back
                  </Btn>
                  <div>
                    <span className="fw-bold">
                      Question {currentIdx + 1} of {total}
                    </span>
                  </div>
                  {currentIdx < total - 1 && (
                    <Btn
                      color="primary"
                      onClick={handleNext}
                      disabled={!isValid(currentQuestion)}
                    >
                      Next
                    </Btn>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center text-muted py-5">
                <H4>No questions available for this survey version.</H4>
              </div>
            )}
          </CardBody>
          <FixedFooter>
            <Btn outline color="primary" onClick={handleClose}>
              Close
            </Btn>
          </FixedFooter>
        </Card>
      </div>
    </div>
  );
};

export default SurveyPreview;
